import '../ui-game-tile/ui-game-tile';
import { html, LitElement, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { type LoginObject, LoginStatus } from '../../../app/package/base/service/login/login-domain';
import {
  getGameType,
  TrackableEventAction,
  TrackingEventSource,
} from '../../../app/package/base/service/tracking/tracking-service';
import {
  FetchPriority,
  LabelValue,
  type LobbyCategoryDisplay,
  type LobbyGame,
  TileType,
} from '../../base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../base/package/patterns/package/store';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../base/package/util/event';
import { formatMoney } from '../../base/package/util/money';
import type { GameTileLabel, GameTileLatestWin } from '../ui-game-tile/ui-game-tile';
import { TILE_SIZE_PX } from '../ui-game-tile/ui-game-tile-sizes';
import '../incentive/view-all-games-button/view-all-games-button';
import './ui-custom-carousel';
import componentStyle from './ui-games-carousel.css?inline';

const CName = 'ui-games-carousel';

// Gap between the two stacked tiles in a double-row carousel item
// (--ui-space-xs). Keep in sync with the `.carousel-item` gap in the
// internal carousel stylesheets.
const ROW_GAP_PX = 8;

// Extra height added below the tile image when a tile renders latest-win
// data (e.g. "Aktuelle Gewinne" lanes): footer block (8px margin-top +
// 22px content + 4px margin-bottom = 34px) + amount (15px). Measured
// against the de-games/de-casino game-tile theme; keep in sync with the
// `.footer` / `.amount` rules in the game-tile theme CSS.
const LATEST_WIN_ROW_PX = 49;

type GameCols = (LobbyGame | undefined)[][];

/**
 * @prop {LobbyCategoryDisplay} display - display options
 * @prop {string} padding - This padding will be applied to the start and end of the carousel.
 * @fires ui-games-carousel-click - Event fired when a game tile is clicked
 */
@customElement(CName)
export class UIGamesCarousel extends LitElement {
  static readonly styles = unsafeCSS(componentStyle);

  @property({ type: Object }) display: LobbyCategoryDisplay = {
    limit: 20,
    rank: false,
    rows: 1,
    shouldShowLabels: true,
    showVideo: false,
    tile: TileType.P1,
  };

  @property({ type: String }) fetchPriority: FetchPriority = FetchPriority.AUTO;
  @property({ type: Array }) games?: LobbyGame[];
  @property({ type: String }) trackingSource: string;
  @property({ type: String }) categorySlug?: string;
  @property({ type: String }) filter?: string;
  @property({ type: Boolean }) incentiveStyle = false;
  @property({ type: Boolean }) isInfoIconShown = true;
  @property({ type: Boolean }) isActionBlockedByIncentive = false; // TODO: rethink name and usage
  @property({ type: String }) padding = '0';
  @property({ attribute: false }) showViewAllGamesButton = false;
  @property({ attribute: false }) viewAllGamesCb?: () => void = undefined;

  @state() private _userSession: LoginObject | undefined;

  private readonly _subHelper = new SubHelper();

  protected willUpdate(changed: PropertyValues) {
    if (changed.has('display') || changed.has('games')) {
      this._reserveLaneHeight();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'list');
    this._subHelper.addSub(window.$app.loginStore, this._updateSession.bind(this), true);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  render() {
    return html`<ui-custom-carousel .padding=${this.padding}>
      ${this._renderGamesUi(this.games)}
    </ui-custom-carousel>`;
  }

  private _renderDoubleRowUi(cols: GameCols, type: TileType, priority: FetchPriority, isRankShown: boolean) {
    return cols.map((col, i) => {
      const rankValue = isRankShown ? i : undefined;
      return html`
        <div class="carousel-item" role="listitem">
          ${this._renderTileUi(col[0]!, type, this._priority(i, priority), rankValue, i)}
          ${
            col[1] !== undefined ? this._renderTileUi(col[1], type, this._priority(i, priority), rankValue, i) : nothing
          }
        </div>
      `;
    });
  }

  private _renderGamesUi(games: LobbyGame[] | undefined) {
    if (!games) {
      return nothing;
    }
    const rows = this.display.rows;
    const tileType = this.display.tile;
    const tilesPerRow = Math.ceil(games.length / rows);
    let isRankShown = this.display.rank;
    if (this.display.rank && rows !== 1) {
      window.$app.logger.warn(`rank option not supported on multiple rows: ${rows} (1 only)`);
      isRankShown = false;
    }
    const tiles: LobbyGame[][] = rows === 1 ? [games] : [games.slice(0, tilesPerRow), games.slice(tilesPerRow)];

    if (games.length === 0) {
      // No games => skip
      window.$app.logger.log('carousel tiles:', games.length, '• no games => skip');
      return nothing;
    }

    let cols: GameCols = [];
    if (rows === 1) {
      cols = [tiles[0]!];
    } else {
      tiles[0]!.forEach((tile, i) => {
        const row1 = tile;
        const row2 = tiles[1]![i];
        cols.push([row1, row2]);
      });
    }

    return html`
      ${
        rows === 1
          ? this._renderSingleRowUi(cols, tileType, this.fetchPriority, isRankShown)
          : this._renderDoubleRowUi(cols, tileType, this.fetchPriority, isRankShown)
      }
    `;
  }

  private _renderSingleRowUi(cols: GameCols, type: TileType, priority: FetchPriority, isRankShown: boolean) {
    return html`
      ${repeat(
        cols[0]!,
        (col) => col?.id,
        (col, i) => html`
          <div class="carousel-item" role="listitem">
            ${this._renderTileUi(col!, type, this._priority(i, priority), isRankShown ? i : undefined, i)}
          </div>
        `,
      )}
      ${this.showViewAllGamesButton ? html`<incentive-view-all-games-button @click=${() => this.viewAllGamesCb?.()}></incentive-view-all-games-button>` : nothing}
    `;
  }

  private _renderTileUi(
    tile: LobbyGame,
    type: TileType,
    priority: FetchPriority,
    rank: number | undefined,
    position: number,
  ) {
    const imageSrc = tile?.img !== null ? tile.img : '';
    const videoSrc = tile?.vid;
    let currentRank = rank;
    if (currentRank !== undefined) {
      // Start at 1
      currentRank++;
    }

    const latestWin: GameTileLatestWin | undefined = tile?.latestWinAmount
      ? {
          multiplier: tile.latestWinMultiplier,
          amount: formatMoney(+tile?.latestWinAmount),
          time: String(Date.parse(tile?.latestWinTime)),
        }
      : undefined;

    return html`
      <ui-game-tile
        .incentiveStyle=${this.incentiveStyle}
        .activeUser=${this._userSession?.loginStatus === LoginStatus.LOGGED_IN}
        .clickAction=${(ev: PointerEvent) => this._handleGameTileClick(ev, tile, position)}
        .gameId=${tile.id}
        .slug=${tile.slug}
        .imageSrc=${imageSrc}
        .isInfoIconShown=${this.isInfoIconShown}
        .infoAction=${(ev: PointerEvent) => this._showGameInfo(ev, tile, position)}
        .isMaintenanceMode=${tile?.maintenance === true}
        .labels=${this._getLabels(tile)}
        .latestWin=${latestWin}
        .priority=${priority}
        .rank=${currentRank}
        .showVideo=${this.display.showVideo ?? false}
        .title=${tile?.title}
        .type=${type}
        .videoSrc=${videoSrc}
        @maintenanceMode=${() => this._handleMaintenanceTileClick(tile, position)}
      ></ui-game-tile>
    `;
  }

  /**
   * Reserve the exact rendered height of this lane via `--_lane-height`,
   * consumed by the `.carousel` class's `contain-intrinsic-size`. Without
   * this, off-screen lanes fall back to a flat placeholder height that
   * mismatches the real height, corrupting scroll restoration on a
   * freshly-built DOM (back-navigation). See IGM-3846.
   */
  private _reserveLaneHeight() {
    const rows = this.display.rows ?? 1;
    const tileHeight = TILE_SIZE_PX[this.display.tile]?.height;
    if (!tileHeight) return;
    // Latest-win tiles render an extra footer + amount below the image.
    const rowExtra = this.games?.some((game) => game.latestWinAmount) ? LATEST_WIN_ROW_PX : 0;
    const height = (tileHeight + rowExtra) * rows + (rows - 1) * ROW_GAP_PX;
    const value = `${height}px`;
    // Only write when the value actually changes. Progressive lobby loading
    // re-binds `.games` on every carousel across several renders; rewriting
    // `contain-intrinsic-size` with an unchanged value needlessly invalidates
    // style and layout on the content-visibility carousels. This is a
    // redundant-write guard only — it does not fix the iOS scroll flicker,
    // which originates from `content-visibility: auto` itself (see IGM-3862).
    if (this.style.getPropertyValue('--_lane-height') !== value) {
      this.style.setProperty('--_lane-height', value);
    }
  }

  private _updateSession(session: LoginObject) {
    if (session.loginStatus === LoginStatus.LOGGED_OUT) {
      this._userSession = undefined;
    } else {
      this._userSession = { ...session };
    }
  }

  private _handleGameTileClick(ev: PointerEvent, game: LobbyGame, position: number) {
    // ui-game-discover-drawer should close without anything else to happen in this case
    const isGameDiscoveryDrawer = this.trackingSource?.includes(TrackingEventSource.GAME_DISCOVERY_DRAWER);
    const hasMatchingGameIdWithActiveGameId = window.$app.routerStore.value.data?.gameId === game.id;

    if (isGameDiscoveryDrawer && hasMatchingGameIdWithActiveGameId) {
      dispatchCustomEvent(this, 'ui-games-carousel-click');
      ev.preventDefault();
      return;
    }

    if (window.$app.loginStore.value.loginStatus === LoginStatus.LOGGED_IN) {
      dispatchCustomEvent(this, 'ui-games-carousel-click', {
        gameId: game.id,
      });

      if (this.isActionBlockedByIncentive) {
        return;
      }

      this._trackGameClick(game, position, TrackableEventAction.GAME_SELECTED);
      window.$app.vaix.updateVaixData({
        categorySlug: this.categorySlug ?? this.trackingSource,
        gameSlug: game.slug,
        ...(isGameDiscoveryDrawer ? {} : { page: window.$app.router.currentLocation().url }),
        position,
      });
      window.$app.router.navigateToGame(true, game.id);
    } else {
      this._showGameInfo(ev, game, position);
    }
  }

  private _showGameInfo(ev: PointerEvent, game: LobbyGame, position: number) {
    dispatchCustomEvent(this, 'ui-games-carousel-click');
    window.$app.logger.log('show game info', game.id);
    const eventAction =
      game?.maintenance === true ? TrackableEventAction.GAME_UNAVAILABLE : TrackableEventAction.GAME_INFO;
    this._trackGameClick(game, position, eventAction);
    this._stopPropagation(ev);
    this._navigateToGameInfo(game.slug);
  }

  private _handleMaintenanceTileClick(game: LobbyGame, position: number) {
    this._trackGameClick(game, position, TrackableEventAction.GAME_UNAVAILABLE_MODAL_SHOWN);
  }

  private _trackGameClick(game: LobbyGame, position: number, eventAction: TrackableEventAction) {
    if (this.trackingSource) {
      window.$app.trackingStore.value.gameSource = this.trackingSource;
    }

    window.$app.content
      .getGameInfo(undefined, game.slug, TileType.L1)
      .then((gameInfo) => {
        const gamePosition = position + 1;
        const gameType = getGameType(game.title);
        const trackingParams = {
          gameCategory: game.labels?.join(' - '),
          gameFilters: this.filter ?? window.$app.trackingStore.value.gameFilters,
          gameName: game.title,
          gamePosition,
          gamePromo: this.display.showVideo ? 'video' : 'image',
          gameProvider: gameInfo.getStudio(),
          gameSource: this.trackingSource,
          winType: undefined,
          gameType,
        };
        window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });
        window.$app.track.gamePlay({ ...trackingParams, eventAction });
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${game.id}'`, err);
      });
  }

  private _navigateToGameInfo(slug: string) {
    window.$app.router.navigateToGameInfo(slug);
  }

  private _stopPropagation(ev: PointerEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }

  // Preserve the caller's priority for the first 4 tiles (max visible
  // without horizontal scroll on mobile), then drop to LOW.
  private _priority(i: number, priority: FetchPriority) {
    return i < 4 ? priority : FetchPriority.LOW;
  }

  private _getLabels(game: LobbyGame): Array<GameTileLabel> {
    const getLabelText = (label: LabelValue) => {
      if (label === LabelValue.EXCLUSIVE) {
        return window.$app.$t.get(I18n.gameInfo.labels.exclusive);
      }

      if (label === LabelValue.NEW) {
        return window.$app.$t.get(I18n.gameInfo.labels.new);
      }

      return window.$app.$t.get(I18n.gameInfo.labels.top10);
    };

    const translatedLabels: Array<GameTileLabel> =
      game.labels?.map((label) => {
        return {
          id: label,
          value: getLabelText(label),
        };
      }) ?? [];

    return translatedLabels;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIGamesCarousel;
  }
}
